import React from 'react'

import {
  AlignItems,
  Body1,
  Box,
  Button,
  ButtonColorVariant,
  ButtonSize,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading3,
  Icon,
  IconVariant,
  JustifyContent,
} from '@gousto-internal/citrus-react'

export type CollapsedProps = {
  boxSize: string
  deliveryDay: string
  address: string
  isExpanded: boolean
  onEditClick: () => void
}

const InfoText = ({ value }: { value: string }) => (
  <Body1 size={[2, null, 6]} color={Color.ColdGrey_800}>
    {value}
  </Body1>
)

const InfoBullet = () => (
  <Body1 size={[2, null, 6]} color={Color.ColdGrey_800} aria-hidden>
    &bull;
  </Body1>
)

type EditButtonProps = {
  isExpanded: boolean
  onClick: () => void
  colorVariant: ButtonColorVariant
  hideLabelWhenExpanded?: boolean
}

const EditButton = ({
  isExpanded,
  onClick,
  colorVariant,
  hideLabelWhenExpanded,
}: EditButtonProps) => {
  const label = isExpanded ? 'Close' : 'Edit'
  const ariaLabel = isExpanded ? 'Close delivery settings' : 'Edit delivery settings'
  const icon = isExpanded ? 'chevron_up' : 'chevron_down'
  const showLabel = !(hideLabelWhenExpanded && isExpanded)

  return (
    <Button
      size={ButtonSize.Medium}
      colorVariant={colorVariant}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-expanded={isExpanded}
    >
      <Box display={Display.Flex} alignItems={AlignItems.Center} gap={1}>
        {showLabel && <Box>{label}</Box>}
        <Icon name={icon} size={6} variant={IconVariant.Inherit} />
      </Box>
    </Button>
  )
}

export const Collapsed = ({
  boxSize,
  deliveryDay,
  address,
  isExpanded,
  onEditClick,
}: CollapsedProps) => {
  return (
    <Box bg={Color.Secondary_50} borderColor={Color.NeutralGrey_300} borderBottomWidth={0.5}>
      <Box
        maxWidth="1600px"
        paddingV={3}
        paddingH={[4, null, 8]}
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceBetween}
        gap={[2, null, 3]}
        style={{ margin: '0 auto' }}
      >
        <Box
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
          alignItems={[AlignItems.FlexStart, null, AlignItems.Baseline]}
          columnGap={3}
          rowGap={1}
          wrap
        >
          <Heading3 size={[4, null, 7]} fontWeight={FontWeight.Bold} color={Color.ColdGrey_800}>
            You&rsquo;re ordering a one-off box for:
          </Heading3>
          <Box display={Display.Flex} gap={1} alignItems={AlignItems.Baseline}>
            <InfoText value={deliveryDay} />
            <InfoBullet />
            <InfoText value={boxSize} />
            <InfoBullet />
            <InfoText value={address} />
          </Box>
        </Box>
        <Box display={[Display.Flex, null, Display.None]}>
          <EditButton
            isExpanded={isExpanded}
            onClick={onEditClick}
            colorVariant={ButtonColorVariant.Tertiary}
            hideLabelWhenExpanded
          />
        </Box>
        <Box display={[Display.None, null, Display.Flex]}>
          <EditButton
            isExpanded={isExpanded}
            onClick={onEditClick}
            colorVariant={ButtonColorVariant.Primary}
          />
        </Box>
      </Box>
    </Box>
  )
}
